// Training Center → Evals tab.
// Paginated list of all scored queries with a search box. Clicking a
// row opens the EvalDrawer (mirrors web/scripts/training-center.js's
// tcOpenEvalDrawer): raw query + meta, generated SQL, per-dimension
// quality scores, feedback, trace ID, and a "View full conversation"
// deep-link.
//
// Replaces web/scripts/training-center.js's tcLoadEvals().

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { AgGridReact } from "ag-grid-react";
import type { ColDef } from "ag-grid-community";
import { useEffect, useMemo, useState } from "react";
import type { OrgScope, QueryEvalRow } from "../../api/training";
import { trainingApi } from "../../api/training";
import {
  ErrorBlock,
  LoadingBlock,
  SourceBadge,
  formatDateTime,
} from "../admin/_shared";
import { EvalDrawer } from "./EvalDrawer";

const PAGE_SIZE = 50;

interface Props {
  bearer: string;
  scope: OrgScope;
}

export function EvalsTab({ bearer, scope }: Props) {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [rows, setRows] = useState<QueryEvalRow[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeRow, setActiveRow] = useState<QueryEvalRow | null>(null);

  // Reset to page 1 on org or search change.
  useEffect(() => {
    setPage(1);
  }, [scope.organization, search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    trainingApi
      .evals(bearer, { ...scope, search: search || undefined, page, page_size: PAGE_SIZE })
      .then((d) => {
        if (cancelled) return;
        setRows(d.items || []);
        setTotal(d.total || 0);
        setLoading(false);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(e);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [bearer, scope, page, search]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const start = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const end = Math.min(page * PAGE_SIZE, total);

  const colDefs = useMemo<ColDef<QueryEvalRow>[]>(
    () => [
      {
        field: "created_at",
        headerName: "Time",
        width: 180,
        sort: "desc",
        valueFormatter: (p) => formatDateTime(p.value as string),
      },
      { field: "raw_query", headerName: "Query", flex: 2, autoHeight: true, wrapText: true },
      { field: "user_email", headerName: "User", width: 180 },
      { field: "organization", headerName: "Org", width: 140 },
      {
        field: "source",
        headerName: "Source",
        width: 100,
        cellRenderer: (p: { value: string }) => <SourceBadge source={p.value} />,
      },
      { field: "datasource_name", headerName: "Datasource", width: 140 },
      {
        field: "overall_score",
        headerName: "Quality",
        width: 100,
        type: "numericColumn",
        cellRenderer: (p: { value: number | null }) => <QualityCell value={p.value} />,
      },
      {
        field: "feedback_rating",
        headerName: "Feedback",
        width: 110,
        cellRenderer: (p: { value: string | null }) => <FeedbackCell value={p.value} />,
      },
    ],
    [],
  );

  return (
    <div className="space-y-4">
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <label className="block text-xs text-neutral-500 mb-1" htmlFor="evals-search">
            Search queries
          </label>
          <input
            id="evals-search"
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") setSearch(searchInput.trim());
            }}
            placeholder="Search query text…"
            className="block w-full max-w-md rounded-md border border-line h-9 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-brand"
          />
        </div>
        <button
          type="button"
          onClick={() => setSearch(searchInput.trim())}
          className="rounded-md bg-brand text-white px-3 h-9 text-sm font-medium hover:bg-brand-600"
        >
          Search
        </button>
      </div>

      {error && <ErrorBlock error={error} />}
      {loading ? (
        <LoadingBlock />
      ) : (
        <>
          <div className="ag-theme-quartz" style={{ width: "100%", height: 560 }}>
            <AgGridReact
              rowData={rows}
              columnDefs={colDefs}
              defaultColDef={{ sortable: true, filter: true, resizable: true }}
              rowStyle={{ cursor: "pointer" }}
              onRowClicked={(e) => {
                // Open the drawer for any row — even rows with no
                // thread_id (mcp / sdk / slack origin) still have a
                // raw_query, SQL, scores, and feedback worth seeing.
                if (e.data) setActiveRow(e.data);
              }}
            />
          </div>
          <div className="flex items-center justify-end gap-3 text-xs text-neutral-600">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="rounded-md border border-line px-3 h-8 hover:bg-chip disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Prev
            </button>
            <span>
              {start}–{end} of {total} · Page {page} of {totalPages}
            </span>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="rounded-md border border-line px-3 h-8 hover:bg-chip disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </>
      )}
      <EvalDrawer
        bearer={bearer}
        scope={scope}
        row={activeRow}
        onClose={() => setActiveRow(null)}
      />
    </div>
  );
}

function QualityCell({ value }: { value: number | null }) {
  if (value == null) return <span className="text-neutral-400">—</span>;
  const pct = Math.round(value * 100);
  const color = value >= 0.7 ? "text-emerald-700" : value >= 0.4 ? "text-amber-700" : "text-red-700";
  return <span className={`font-semibold ${color}`}>{pct}%</span>;
}

function FeedbackCell({ value }: { value: string | null }) {
  if (value === "Good")
    return <span className="text-emerald-700 text-xs font-medium">👍 Good</span>;
  if (value === "Bad") return <span className="text-red-700 text-xs font-medium">👎 Bad</span>;
  return <span className="text-neutral-400">—</span>;
}
